import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button, Card, Input, Spinner } from '../components/ui';
import { toast } from '../utils/toastBus';
import { BrandContext } from '../context/BrandContext';

const DEFAULT_PROFILE = Object.freeze({
  name: '',
  tagline: '',
  mission: '',
  tone: '',
  primaryColor: '#ff5a1f',
  secondaryColor: '#ff5a1f',
  websiteUrl: '',
  socials: '',
});

const DEFAULT_IMPORT = Object.freeze({
  url: '',
  assetHints: '',
});

export default function BrandWorkspace() {
  const navigate = useNavigate();
  const { profile, profileExists, loading: brandLoading, refresh: refreshBrandProfile, setProfile: setBrandProfile } = useContext(BrandContext);
  const [profileDraft, setProfileDraft] = useState(DEFAULT_PROFILE);
  const [importForm, setImportForm] = useState(DEFAULT_IMPORT);
  const [importStatus, setImportStatus] = useState(null);
  const [busy, setBusy] = useState({ saving: false, importing: false });
  const pollRef = useRef(null);

  const hasProfile = useMemo(() => profileExists, [profileExists]);

  const mergeProfileDraft = useCallback((nextProfile) => {
    setProfileDraft((prev) => ({
      ...prev,
      ...Object.keys(DEFAULT_PROFILE).reduce((acc, key) => {
        if (key === 'socials') {
          const socials = nextProfile?.socials;
          if (Array.isArray(socials)) {
            acc.socials = socials.join('\n');
          } else if (typeof socials === 'string') {
            acc.socials = socials;
          }
          return acc;
        }
        if (nextProfile?.[key]) acc[key] = nextProfile[key];
        return acc;
      }, {}),
    }));
  }, []);

  useEffect(() => {
    if (profile) {
      mergeProfileDraft(profile);
    } else {
      setProfileDraft(DEFAULT_PROFILE);
    }
  }, [profile, mergeProfileDraft]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const stopImportPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startImportPolling = useCallback((importId) => {
    stopImportPolling();
    if (!importId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.brandImportStatus(importId);
        const status = res?.status || res?.item?.status || 'pending';
        setImportStatus((prev) => ({ ...prev, status, detail: res }));
        if (status === 'ready' || status === 'error') {
          stopImportPolling();
          if (res?.profile || res?.item?.profile) {
            const nextProfile = res.profile || res.item?.profile;
            setBrandProfile(nextProfile);
            mergeProfileDraft(nextProfile);
            refreshBrandProfile?.();
          }
        }
      } catch {
        // swallow polling errors; retry on next interval
      }
    }, 2500);
  }, [mergeProfileDraft, stopImportPolling]);

  const onSaveProfile = useCallback(async () => {
    const payload = {
      ...profileDraft,
      socials: profileDraft.socials
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    };
    setBusy((b) => ({ ...b, saving: true }));
    try {
      const res = await api.brandSaveProfile(payload);
      if (res?.error) {
        toast({ type: 'error', message: res.error || 'Failed to save brand profile' });
        return;
      }
      const saved = res?.item || payload;
      setBrandProfile(saved);
      refreshBrandProfile?.();
      toast({ type: 'success', message: 'Brand profile saved' });
    } finally {
      setBusy((b) => ({ ...b, saving: false }));
    }
  }, [profileDraft, refreshBrandProfile, setBrandProfile]);

  const onStartImport = useCallback(async () => {
    const url = String(importForm.url || '').trim();
    if (!url) {
      toast({ type: 'warning', message: 'Enter a site or brand URL to import' });
      return;
    }
    setBusy((b) => ({ ...b, importing: true }));
    try {
      const payload = {
        url,
        assetHints: importForm.assetHints,
      };
      const res = await api.brandStartImport(payload);
      if (res?.error) {
        toast({ type: 'error', message: res.error || 'Import failed to start' });
        return;
      }
      const importId = res?.importId || res?.item?.id || res?.id;
      setImportStatus({ id: importId, status: 'queued' });
      startImportPolling(importId);
      toast({ type: 'info', message: 'Brand import started. We will surface assets soon.' });
    } finally {
      setBusy((b) => ({ ...b, importing: false }));
    }
  }, [importForm.assetHints, importForm.url, startImportPolling]);

  const primaryCtaLabel = hasProfile ? 'Open Projects' : 'Start Quick Start';
  const primaryCtaHandler = useCallback(() => {
    navigate(hasProfile ? '/projects' : '/quickstart');
  }, [hasProfile, navigate]);

  const importAssetPlaceholders = useMemo(() => {
    const detailAssets = importStatus?.detail?.assets;
    if (Array.isArray(detailAssets) && detailAssets.length) {
      return detailAssets.map((asset, idx) => ({
        id: asset.id || asset.slug || `asset-${idx}`,
        label: asset.label || asset.type || 'Asset',
        status: asset.status || importStatus?.status || 'pending',
        description: asset.description || 'Ready for review',
      }));
    }
    return [
      { id: 'logos', label: 'Logos & wordmarks', status: importStatus?.status || 'pending', description: 'We will capture SVG/PNG versions of your logo for re-use in ads.' },
      { id: 'palette', label: 'Color palette', status: importStatus?.status || 'pending', description: 'Primary & secondary colors extracted from your website for automatic theming.' },
      { id: 'copy', label: 'Voice snippets', status: importStatus?.status || 'pending', description: 'Taglines, CTAs, and mission copy ready for scripts.' },
      { id: 'socials', label: 'Social profiles', status: importStatus?.status || 'pending', description: 'Links and handles so we can tailor channel-specific plans.' },
    ];
  }, [importStatus]);

  return (
    <main style={{ minHeight: '100vh', background: 'var(--gradient-hero)' }}>
      <div className="container" style={{ padding: '32px 0 80px' }}>
        <header style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <p style={{ color: '#ff7a45', letterSpacing: 1, textTransform: 'uppercase', fontSize: 12 }}>Brand Workspace</p>
            <h1 style={{ fontSize: 36, margin: 0 }}>Turn your brand into a marketing engine</h1>
            <p style={{ color: 'var(--color-text-muted)', maxWidth: 720 }}>
              Create a new brand identity or import an existing presence. We will secure your colors, logos, copy, and tone so every ad, stream, or campaign feels on brand.
            </p>
          </div>
          <Button size="lg" variant="primary" onClick={primaryCtaHandler}>
            {primaryCtaLabel}
          </Button>
        </header>

        {brandLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <Spinner label="Loading brand workspace" />
          </div>
        ) : (
          <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            <Card title="Brand snapshot" subtitle={hasProfile ? 'Active profile ready for every tool.' : 'Complete your profile to unlock automation.'} hover>
              {hasProfile ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{profile?.name || 'Untitled brand'}</h3>
                    <p style={{ margin: '4px 0', color: 'var(--color-text-muted)' }}>{profile?.tagline || 'Add a tagline to inspire creative.'}</p>
                  </div>
                  {profile?.mission && (
                    <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
                      {profile.mission}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Primary</span>
                      <div style={{ background: profile?.primaryColor || '#ff5a1f', borderRadius: 8, padding: 12, marginTop: 4 }}>
                        <code style={{ fontSize: 12 }}>{profile?.primaryColor || '#ff5a1f'}</code>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Secondary</span>
                      <div style={{ background: profile?.secondaryColor || '#ff5a1f', borderRadius: 8, padding: 12, marginTop: 4 }}>
                        <code style={{ fontSize: 12 }}>{profile?.secondaryColor || '#ff5a1f'}</code>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => navigate('/quickstart')}>
                    Use brand in Quick Start →
                  </Button>
                </div>
              ) : (
                <div>
                  <p style={{ color: 'var(--color-text-muted)' }}>
                    We will summarize your brand DNA here once you complete the form.
                  </p>
                  <Button variant="secondary" onClick={() => navigate('/brand')}>
                    Fill out brand profile
                  </Button>
                </div>
              )}
            </Card>
            <Card title="Create a new brand" subtitle="Define your mission and let AI generate the essentials." hover>
              <div style={{ display: 'grid', gap: 12 }}>
                <Input
                  label="Brand name"
                  placeholder="Loudest Labs"
                  value={profileDraft.name}
                  onChange={(e) => setProfileDraft((prev) => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  label="Tagline"
                  placeholder="AI Audio for bold brands"
                  value={profileDraft.tagline}
                  onChange={(e) => setProfileDraft((prev) => ({ ...prev, tagline: e.target.value }))}
                />
                <label className="input-label" style={{ display: 'grid', gap: 6 }}>
                  <span>Mission</span>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={profileDraft.mission}
                    onChange={(e) => setProfileDraft((prev) => ({ ...prev, mission: e.target.value }))}
                    placeholder="Share what the brand stands for, target audiences, and promises."
                  />
                </label>
                <Input
                  label="Voice & tone"
                  placeholder="Playful, confident, data-backed"
                  value={profileDraft.tone}
                  onChange={(e) => setProfileDraft((prev) => ({ ...prev, tone: e.target.value }))}
                />
                <div style={{ display: 'grid', gap: 8 }}>
                  <span className="input-label">Color palette</span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
                    <label className="color-swatch">
                      <span>Primary</span>
                      <input
                        type="color"
                        value={profileDraft.primaryColor}
                        onChange={(e) => setProfileDraft((prev) => ({ ...prev, primaryColor: e.target.value }))}
                      />
                    </label>
                    <label className="color-swatch">
                      <span>Secondary</span>
                      <input
                        type="color"
                        value={profileDraft.secondaryColor}
                        onChange={(e) => setProfileDraft((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                      />
                    </label>
                  </div>
                </div>
                <Input
                  label="Website URL (optional)"
                  placeholder="https://loudest.ai"
                  value={profileDraft.websiteUrl}
                  onChange={(e) => setProfileDraft((prev) => ({ ...prev, websiteUrl: e.target.value }))}
                />
                <label className="input-label" style={{ display: 'grid', gap: 6 }}>
                  <span>Social handles (one per line)</span>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={profileDraft.socials}
                    onChange={(e) => setProfileDraft((prev) => ({ ...prev, socials: e.target.value }))}
                    placeholder="@brand on Instagram\nhttps://linkedin.com/company/brand"
                  />
                </label>
                <Button
                  variant="primary"
                  onClick={onSaveProfile}
                  disabled={busy.saving}
                  style={{ marginTop: 8 }}
                >
                  {busy.saving ? 'Saving…' : hasProfile ? 'Update brand profile' : 'Create brand profile'}
                </Button>
              </div>
            </Card>

            <Card title="Import an existing brand" subtitle="Paste a URL and we will fetch logos, colors, and copy." hover>
              <div style={{ display: 'grid', gap: 12 }}>
                <Input
                  label="Brand website"
                  placeholder="https://example.com"
                  value={importForm.url}
                  onChange={(e) => setImportForm((prev) => ({ ...prev, url: e.target.value }))}
                />
                <label className="input-label" style={{ display: 'grid', gap: 6 }}>
                  <span>Optional guidance</span>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={importForm.assetHints}
                    onChange={(e) => setImportForm((prev) => ({ ...prev, assetHints: e.target.value }))}
                    placeholder="Link to brand kits, Dropbox folders, or list must-have assets."
                  />
                </label>
                <Button
                  variant="secondary"
                  onClick={onStartImport}
                  disabled={busy.importing}
                >
                  {busy.importing ? 'Importing…' : 'Start import'}
                </Button>
                {importStatus && (
                  <div className="muted" style={{ fontSize: 14 }}>
                    Import status: <strong>{importStatus.status}</strong>
                  </div>
                )}
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
                  {importAssetPlaceholders.map((asset) => (
                    <li key={asset.id} style={{ border: '1px solid var(--color-border, #374151)', borderRadius: 8, padding: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>{asset.label}</span>
                        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{asset.status}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)' }}>{asset.description}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            <Card title="What happens next" subtitle="Your workspace feeds every other tool." hover>
              <ul style={{ color: 'var(--color-text-muted)', fontSize: 14, lineHeight: 1.6, paddingLeft: 20 }}>
                <li>Ad Studio pulls your saved palette, logos, and tone for instant scripts.</li>
                <li>Quick Start auto-fills project context so you can generate assets in one click.</li>
                <li>Imported assets appear in your composer library for reuse across campaigns.</li>
              </ul>
              {hasProfile && (
                <Button variant="ghost" onClick={() => navigate('/projects')} style={{ marginTop: 16 }}>
                  Jump to Projects →
                </Button>
              )}
            </Card>
          </div>
        )}
      </div>
      <style jsx>{`
        .textarea {
          width: 100%;
          border-radius: var(--border-radius-md);
          border: 1px solid var(--color-border, #374151);
          background: rgba(15, 23, 42, 0.8);
          color: var(--color-text-primary, #f8fafc);
          padding: 10px 12px;
          resize: vertical;
          min-height: 96px;
        }
        .textarea:focus {
          outline: none;
          border-color: var(--color-primary, #ff5a1f);
          box-shadow: 0 0 0 1px var(--color-primary, #ff5a1f);
        }
        .color-swatch {
          display: grid;
          gap: 6px;
          font-size: 12px;
          color: var(--color-text-muted);
        }
        .color-swatch input[type='color'] {
          width: 100%;
          height: 48px;
          border: none;
          border-radius: var(--border-radius-md);
          cursor: pointer;
          background: transparent;
        }
      `}</style>
    </main>
  );
}
